import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const tabBase =
  "inline-flex min-h-11 shrink-0 cursor-pointer items-center justify-center gap-2 text-fluid-sm font-semibold whitespace-nowrap transition focus-visible:outline-2 focus-visible:outline-brand-600";

/**
 * Two looks for one behaviour. `segmented` is a grey track with the active tab
 * lifted onto a white pill — whole sections of a page. `underline` is the
 * lighter strip for a choice inside one card.
 *
 * Each track scrolls sideways on its own when the labels outrun a phone's width
 * ("Limbă și fus orar" does at 360px), instead of widening the page. Its
 * scrollbar is hidden — a desktop browser draws a thick grey bar under a strip
 * this short — and the label cut off at the edge is the cue that there is more.
 * The focus ring of the underline tabs is drawn inward, because the scroll
 * container would clip one drawn outside the tab.
 */
const VARIANTS = {
  segmented: {
    track: "overflow-x-auto rounded-xl bg-ink-100 p-1 [scrollbar-width:none]",
    list: "flex w-max min-w-full gap-1",
    tab: (active) =>
      `${tabBase} flex-1 rounded-lg px-4 focus-visible:outline-offset-2 ${
        active
          ? "bg-white text-brand-700 shadow-card"
          : "text-ink-600 hover:text-ink-900"
      }`,
  },
  underline: {
    track: "overflow-x-auto [scrollbar-width:none]",
    list: "flex w-max min-w-full gap-6 border-b border-ink-200",
    tab: (active) =>
      `${tabBase} -mb-px border-b-2 px-0.5 focus-visible:-outline-offset-2 ${
        active
          ? "border-brand-600 text-brand-700"
          : "border-transparent text-ink-500 hover:text-ink-900"
      }`,
  },
};

const tabId = (idPrefix, id) => `${idPrefix}-tab-${id}`;
const panelId = (idPrefix, id) => `${idPrefix}-panel-${id}`;

/**
 * A WAI-ARIA tab list: the arrow keys and Home/End move between tabs (and
 * select them — every panel here is already rendered, so there is nothing to
 * wait for), and only the selected tab is in the Tab order.
 *
 * Pair with `TabPanel`, which carries the matching ids.
 */
const TabList = ({ tabs, active, onChange, label, idPrefix, variant = "segmented" }) => {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);
  const tabRefs = useRef({});
  const styles = VARIANTS[variant];

  // A tab selected from the URL can sit past the edge of a scrolled track on a
  // phone. Scroll the track — never the page — until it shows.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const tab = tabRefs.current[active];
    if (!scroller || !tab) return;
    const track = scroller.getBoundingClientRect();
    const box = tab.getBoundingClientRect();
    if (box.left < track.left) scroller.scrollLeft -= track.left - box.left;
    else if (box.right > track.right) scroller.scrollLeft += box.right - track.right;
  }, [active]);

  const move = (event, index) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    const edge = { Home: 0, End: tabs.length - 1 }[event.key];
    if (step === undefined && edge === undefined) return;
    event.preventDefault();
    const next = edge ?? (index + step + tabs.length) % tabs.length;
    const { id } = tabs[next];
    onChange(id);
    tabRefs.current[id]?.focus();
  };

  return (
    <div ref={scrollerRef} className={styles.track}>
      <div role="tablist" aria-label={label} className={styles.list}>
        {tabs.map(({ id, icon, labelKey }, index) => (
          <button
            key={id}
            ref={(element) => {
              tabRefs.current[id] = element;
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, id)}
            aria-selected={active === id}
            aria-controls={panelId(idPrefix, id)}
            tabIndex={active === id ? 0 : -1}
            onClick={() => onChange(id)}
            onKeyDown={(event) => move(event, index)}
            className={styles.tab(active === id)}
          >
            {icon && <i className={`bi ${icon}`} aria-hidden="true" />}
            {t(labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
};

TabList.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.string,
      labelKey: PropTypes.string.isRequired,
    }),
  ).isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  idPrefix: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(["segmented", "underline"]),
};

/**
 * One tab's content. Hidden rather than unmounted when another tab is
 * selected, so a half-typed field survives a look at the next tab.
 */
export const TabPanel = ({ idPrefix, id, active, className, children }) => (
  <div
    role="tabpanel"
    id={panelId(idPrefix, id)}
    aria-labelledby={tabId(idPrefix, id)}
    hidden={active !== id}
    className={className}
  >
    {children}
  </div>
);

TabPanel.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  active: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default TabList;
